'use client'

import { useEffect } from 'react'

const fontsOrigin = (process.env.NEXT_PUBLIC_FONTS_URL || 'https://centrally-relished-dormouse.cloudpub.ru').replace(/\/$/, '')
const styleElementId = 'vcentre-font-catalog'

type CatalogFace = {
  format?: 'woff2' | 'truetype'
  path: string
  style: 'normal' | 'italic'
  weight: string
}

type CatalogFamily = {
  cssFamily: string
  faces: CatalogFace[]
}

type CatalogResponse = {
  families?: CatalogFamily[]
}

function escapeCssString(value: string) {
  return value.replace(/[\\"\n\r\f]/g, (character) => `\\${character}`)
}

export default function FontCatalogLoader() {
  useEffect(() => {
    const controller = new AbortController()

    async function loadCatalog() {
      try {
        const response = await fetch(`${fontsOrigin}/fonts/catalog.json`, {
          cache: 'no-store',
          credentials: 'omit',
          signal: controller.signal,
        })
        if (!response.ok) return

        const catalog = (await response.json()) as CatalogResponse
        const rules = (catalog.families || [])
          .flatMap((family) =>
            (family.faces || []).map((face) => {
              const url = new URL(face.path, `${fontsOrigin}/`).toString()
              const format = face.format === 'truetype' ? 'truetype' : 'woff2'
              return `@font-face{font-family:"${escapeCssString(family.cssFamily)}";src:url("${url}") format("${format}");font-style:${face.style === 'italic' ? 'italic' : 'normal'};font-weight:${face.weight};font-display:swap;}`
            }),
          )
          .join('\n')

        if (!rules) return

        let style = document.getElementById(styleElementId) as HTMLStyleElement | null
        if (!style) {
          style = document.createElement('style')
          style.id = styleElementId
          document.head.appendChild(style)
        }
        style.textContent = rules
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.warn('Unable to load the Vcentre font catalog', error)
        }
      }
    }

    loadCatalog()
    return () => controller.abort()
  }, [])

  return null
}
